import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MAX_MARKERS,
  REPOS_MIN_MS,
  SCAN_COOLDOWN,
  createArucoDetector,
  mesurerOrientation,
  deciderLecture,
  drawMarkerOverlay,
  tailleAnalyse,
  LARGEUR_DEPART,
  INTERVALLE_REGLAGE_MS,
  reglerLargeur,
} from '../utils/aruco';

/**
 * Caméra + boucle de détection ArUco, moteur de la vue scan (ScanMobile,
 * seule vue de passation : téléphone comme desktop/webcam).
 *
 * Règle de comptage (2026-09-19, cf. deciderLecture dans utils/aruco.js) :
 * une carte est comptée à 2 lectures identiques parmi ses 3 dernières. Son
 * compteur ne reçoit que SES lectures (jamais de ratés : une image sans la
 * carte ne change rien) et n'est vidé qu'au changement de question. Une
 * carte vue une fois par passage finit donc par être comptée, le prof balaye
 * par bloc. Avant : 4 lectures identiques parmi les 6 dernières analyses, et
 * un compteur vidé après 6 analyses sans la carte, ce qui imposait de rester
 * sur chaque carte jusqu'au bip. Cadence : un repos égal à la durée de
 * l'analyse précédente (plancher REPOS_MIN_MS), cooldown 400 ms par carte.
 *
 * @param {object} params
 * @param {object} params.videoRef      ref du <video> du consommateur
 * @param {object} params.canvasRef     ref du <canvas> (sert AUSSI à lire les
 *                                      pixels : requis même sans overlay)
 * @param {boolean} params.active       caméra + détection actives
 * @param {boolean} [params.paused=false] suspend la DÉTECTION en laissant la
 *                                      caméra tourner (ex. question pas encore
 *                                      lancée) — évite un redémarrage caméra
 * @param {Set}    params.scannedCodes  codes déjà comptés (vidé à chaque
 *                                      question → reset des buffers internes)
 * @param {Function} params.onScan      ({ code_aruco, rotation }) — scan validé,
 *                                      À BATCHER par le consommateur.
 *                                      ROTATION, pas lettre (2026-09-12) :
 *                                      chaque carte a sa propre disposition
 *                                      des lettres (anti-copie sur le voisin),
 *                                      seul le serveur sait traduire.
 *                                      rotation = r : le côté n° r de la carte
 *                                      est en haut (haut 0, droite 1, bas 2,
 *                                      gauche 3) ; disposition[r] est la
 *                                      lettre imprimée sur ce côté.
 * @param {Function} [params.onDetection] ({ code_aruco, reponse, corners,
 *                                      videoWidth, videoHeight }) — position
 *                                      du badge « ✓ Prénom », au scan validé
 * @param {boolean} [params.drawOverlay=true] contours sur le canvas (vert
 *                                      comptée, jaune en cours)
 * @param {number|null} [params.largeurForcee=null] grand côté de l'image
 *                                      ANALYSÉE choisi par le prof (1920, 1280,
 *                                      960 ; cf. tailleAnalyse). null = RÉGLAGE
 *                                      AUTOMATIQUE (lot C, 2026-09-22) : départ
 *                                      720p, puis toutes les 3 s un cran vers le
 *                                      bas si la médiane des dix dernières
 *                                      analyses dépasse 150 ms, un cran vers le
 *                                      haut sous 60 ms (reglerLargeur). La
 *                                      caméra et l'aperçu restent en 1080p,
 *                                      seule l'image lue par le détecteur
 *                                      change ; contours, badges et vote n'en
 *                                      dépendent pas, changer en pleine
 *                                      question est sûr.
 *
 * @returns {{ cameraActive, cameraError, playBeep, torchSupported, torchOn,
 *             toggleTorch, restart, largeurAnalyse }} largeurAnalyse = le grand
 *             côté effectivement analysé (forcé ou automatique), pour la
 *             pastille du bandeau.
 *
 * Banc d'essai des 21-22/09 (panneau de mesures, trois postes dessin /
 * lecture / détecteur, lues / retenues) : retiré une fois la règle calée,
 * cf. commits 4a0e13cb, 1f38e781 et l'historique de ce fichier. Ce qu'il a
 * appris : le détecteur js-aruco2 coûte en proportion des pixels (téléphone
 * 290 ms sur 375 en 1080p), la lecture des pixels se paie en copie de
 * l'image vidéo sur un canvas en lecture directe (tablette 80 à 130 ms sur
 * 160 à 200), et le 540p mal appliqué en portrait faisait croire à un coût
 * fixe.
 */
export function useArucoScanner({
  videoRef,
  canvasRef,
  active,
  paused = false,
  scannedCodes,
  onScan,
  onDetection,
  drawOverlay = true,
  largeurForcee = null,
}) {
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  // Taille analysée : forcée par le prof, sinon automatique (lot C). La
  // boucle lit les refs pour ne jamais redémarrer ; l'état n'existe que pour
  // la pastille du bandeau.
  const largeurForceeRef = useRef(largeurForcee);
  largeurForceeRef.current = largeurForcee;
  const largeurAutoRef = useRef(LARGEUR_DEPART);
  const [largeurAnalyse, setLargeurAnalyse] = useState(LARGEUR_DEPART);
  const prochainReglageRef = useRef(0);
  const dureesRef = useRef([]);            // durées (ms) des dix dernières analyses à la taille courante
  // Plus d'état « cartes dans le cadre » : personne ne le lisait, et sa mise à
  // jour à chaque analyse redessinait tout l'écran de scan pour rien
  // (2026-09-19). Ce qui change à l'écran passe par onScan / onDetection.
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const detectorRef = useRef(null);
  const buffersRef = useRef(new Map());
  const cooldownsRef = useRef(new Map());
  const audioCtxRef = useRef(null);
  // Cadence : un REPOS au moins égal à la durée de l'analyse qui vient de
  // finir (plancher REPOS_MIN_MS). Le fil principal est libre la moitié du
  // temps sur tout téléphone : un rapide (20 ms) analyse ~14 fois par seconde,
  // un lent (300 ms) ~2 fois, sans jamais saturer l'aperçu ni les boutons.
  // Avant : une image sur deux, et sur un téléphone lent l'analyse suivante
  // s'enchaînait aussitôt, fil occupé en permanence (« hyper lent »).
  const prochaineAnalyseRef = useRef(0);

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onDetectionRef = useRef(onDetection);
  onDetectionRef.current = onDetection;
  const scannedCodesRef = useRef(scannedCodes);
  scannedCodesRef.current = scannedCodes;

  useEffect(() => {
    detectorRef.current = createArucoDetector();
  }, []);

  // Reset des refs internes quand scannedCodes est vidé (changement de question)
  useEffect(() => {
    if (scannedCodes.size === 0) {
      buffersRef.current = new Map();
      cooldownsRef.current = new Map();
    }
  }, [scannedCodes]);

  // Taille forcée par le prof, ou rendue à l'automatique : les durées de
  // l'ancienne taille ne disent rien de la nouvelle, on repart de zéro, et la
  // pastille affiche la taille effective.
  useEffect(() => {
    dureesRef.current = [];
    setLargeurAnalyse(largeurForcee ?? largeurAutoRef.current);
  }, [largeurForcee]);

  const playBeep = useCallback((type = 'scan') => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === 'scan') {
        osc.frequency.value = 880;
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ctx.currentTime + 0.08);
      } else {
        osc.frequency.value = 660;
        gain.gain.value = 0.15;
        osc.start();
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
        osc.stop(ctx.currentTime + 0.25);
      }
    } catch {
      // AudioContext non disponible
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      // 1080p SOUHAITÉ (ideal, jamais exact : un téléphone qui plafonne à 720p
      // continue en 720p sans échec). À 3 m, 1280 px de large font 3,5 px/cm,
      // soit ~6 px par case pour une carte demi-page, sur le fil ; en 1920 px
      // on passe à 5,2 px/cm et ~9 px par case, confortable. Le détecteur
      // travaille sur l'image native : 2,25 fois plus de pixels par image,
      // quelques images par seconde de moins, sans gravité, le scan accumule.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
      // Torche (Android/Chrome surtout) — silencieux si non supporté
      try {
        const track = stream.getVideoTracks()[0];
        setTorchSupported(Boolean(track?.getCapabilities?.().torch));
      } catch {
        setTorchSupported(false);
      }
      setTorchOn(false);
    } catch (err) {
      setCameraError('Impossible d\'accéder à la caméra');
      setCameraActive(false);
    }
  }, [videoRef]);

  const stopCamera = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setTorchSupported(false);
    setTorchOn(false);
  }, [videoRef]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      // torche non appliquée (capacité annoncée mais refusée) — sans gravité
    }
  }, [torchOn]);

  const restart = useCallback(() => {
    stopCamera();
    startCamera();
  }, [stopCamera, startCamera]);

  // Cycle de vie caméra
  useEffect(() => {
    if (active) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [active, startCamera, stopCamera]);

  // Boucle de détection
  useEffect(() => {
    if (!cameraActive || !detectorRef.current || !active || paused) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Lecture directe : ce canvas sert d'abord à LIRE les pixels de l'image
    // vidéo (l'image est effacée aussitôt, la vidéo en dessous s'affiche
    // seule, il ne garde que quelques contours). Sans l'option, le navigateur
    // le garde dans la carte graphique et chaque lecture est un trajet carte
    // graphique → processeur, lent sur un téléphone d'entrée de gamme, à
    // refaire à chaque analyse quelle que soit la taille de l'image. Un
    // navigateur qui ne connaît pas l'option l'ignore.
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const detect = () => {
      const debut = performance.now();
      if (debut < prochaineAnalyseRef.current || !video.videoWidth) {
        animFrameRef.current = requestAnimationFrame(detect);
        return;
      }

      // L'image analysée : réduite au grand côté choisi (forcé par le prof,
      // sinon le réglage automatique), ou native. Le canvas prend cette
      // taille ; il couvre le même cadre que la vidéo (object-cover), donc
      // contours et badges, calculés dans le repère de l'image analysée,
      // tombent au bon endroit à l'écran.
      const taille = tailleAnalyse(video.videoWidth, video.videoHeight, largeurForceeRef.current ?? largeurAutoRef.current);
      // La taille n'est posée que si elle change : écrire canvas.width, même à
      // l'identique, jette le canvas et en refabrique un, une allocation par
      // analyse pour rien (repéré le 19/09). Un téléphone qui tourne ou un
      // passage en 540p sont toujours suivis.
      if (canvas.width !== taille.width || canvas.height !== taille.height) {
        canvas.width = taille.width;
        canvas.height = taille.height;
      }
      // La copie de l'image ne sert qu'à lire les pixels : le canvas est
      // effacé aussitôt et ne porte que les contours, la VIDÉO en dessous
      // s'affiche en direct quelle que soit la cadence d'analyse. Avant, le
      // canvas restait opaque : le prof regardait la dernière image analysée.
      ctx.drawImage(video, 0, 0, taille.width, taille.height);
      const imageData = ctx.getImageData(0, 0, taille.width, taille.height);
      ctx.clearRect(0, 0, taille.width, taille.height);
      const markers = detectorRef.current.detect(imageData);

      const validMarkers = markers.filter(m => m.id < MAX_MARKERS);

      let compteesCetteAnalyse = 0;
      // Un numéro par image : la garantie « jamais comptée sur une seule
      // image » suppose qu'une carte n'y apparaît qu'une fois. Si le même
      // numéro y est décodé deux fois (reflet dans une vitre), seule la
      // première lecture entre au compteur.
      const vusCetteAnalyse = new Set();
      for (const marker of validMarkers) {
        if (vusCetteAnalyse.has(marker.id)) continue;
        vusCetteAnalyse.add(marker.id);
        const alreadyScanned = scannedCodesRef.current.has(marker.id);
        if (drawOverlay) drawMarkerOverlay(ctx, marker, alreadyScanned ? 'already' : 'detecting');

        // Carte déjà comptée (ou en gel de 400 ms, le temps qu'elle le soit) :
        // son contour vert suffit, aucun badge, aucun calcul. Le temps d'une
        // question ne dépend que des cartes PAS ENCORE comptées.
        if (alreadyScanned || cooldownsRef.current.get(marker.id)) continue;

        // Une lecture, une décision (cf. deciderLecture) : vote 2 sur 3 pour
        // toute lecture franche, rien en losange, voie rapide fermée. Le
        // compteur d'une carte ne reçoit que ses lectures, jamais de ratés,
        // et n'est vidé qu'au changement de question : une carte vue une
        // fois par passage finit par être comptée, le balayage marche.
        const { reponse, franche } = mesurerOrientation(marker.corners);
        const decision = deciderLecture(
          { reponse, franche, distance: marker.hammingDistance },
          buffersRef.current.get(marker.id) || [],
        );
        if (!decision.comptee) {
          buffersRef.current.set(marker.id, decision.compteur);
          continue;
        }

        cooldownsRef.current.set(marker.id, true);
        buffersRef.current.delete(marker.id);
        // La lettre est le côté en haut (A haut, B droite, C bas, D gauche) :
        // son index est la rotation attendue par le serveur.
        const rotation = 'ABCD'.indexOf(decision.reponse);
        onScanRef.current({ code_aruco: marker.id, rotation });
        onDetectionRef.current?.({
          code_aruco: marker.id, rotation, corners: marker.corners,
          // le repère des coins est l'image analysée, pas la vidéo
          videoWidth: taille.width, videoHeight: taille.height,
        });
        compteesCetteAnalyse += 1;
        setTimeout(() => cooldownsRef.current.delete(marker.id), SCAN_COOLDOWN);
      }

      // UN bip par analyse : un bloc entier est souvent compté sur la même
      // image, et huit bips superposés ne disent plus rien.
      if (compteesCetteAnalyse > 0) playBeep('scan');

      const duree = performance.now() - debut;
      prochaineAnalyseRef.current = performance.now() + Math.max(REPOS_MIN_MS, duree);

      // Réglage automatique (lot C, cf. reglerLargeur) : la durée de chaque
      // analyse est gardée (dix au plus) ; toutes les 3 s, si le prof n'a
      // rien forcé, un cran vers le bas ou vers le haut sur la médiane, jamais
      // plus. Au changement, les durées de l'ancienne taille ne disent rien
      // de la nouvelle : on repart de zéro.
      dureesRef.current.push(duree);
      if (dureesRef.current.length > 10) dureesRef.current.shift();
      const fin = performance.now();
      if (largeurForceeRef.current == null && fin >= prochainReglageRef.current) {
        prochainReglageRef.current = fin + INTERVALLE_REGLAGE_MS;
        const nouvelle = reglerLargeur(largeurAutoRef.current, dureesRef.current);
        if (nouvelle !== largeurAutoRef.current) {
          largeurAutoRef.current = nouvelle;
          dureesRef.current = [];
          setLargeurAnalyse(nouvelle);
        }
      }
      animFrameRef.current = requestAnimationFrame(detect);
    };

    animFrameRef.current = requestAnimationFrame(detect);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [cameraActive, active, paused, drawOverlay, playBeep, videoRef, canvasRef]);

  return { cameraActive, cameraError, playBeep, torchSupported, torchOn, toggleTorch, restart, largeurAnalyse };
}

export default useArucoScanner;
